import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  BookOpen, Users, Trophy, PenLine, Loader2, 
  RefreshCw, BookMarked
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const ICONS = {
  new_work: PenLine,
  new_chapter: BookMarked,
  new_group: Users,
  challenge_entry: Trophy,
};

const ActivityFeed = () => {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeed = useCallback(async () => {
    try {
      const res = await api.get('/activity-feed?limit=15');
      setActivities(res.data);
    } catch (e) {
      // Non-critical error suppressed
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const getLink = (activity) => {
    if (activity.data?.story_id) return `/story/${activity.data.story_id}`;
    if (activity.data?.group_id) return `/groups/${activity.data.group_id}`;
    if (activity.data?.challenge_id) return `/challenges/${activity.data.challenge_id}`;
    return '#';
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <Card className="border-brand-primary/10">
        <CardContent className="p-5 flex items-center justify-center h-40">
          <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-brand-primary/10" data-testid="activity-feed">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-semibold text-text-primary flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-brand-accent" />
            What's New
          </h3>
          <Button variant="ghost" size="sm" onClick={fetchFeed} className="text-text-muted">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {activities.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            No activity yet. Be the first to publish something!
          </p>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => {
              const Icon = ICONS[activity.type] || PenLine;
              return (
                <Link
                  key={activity.id}
                  to={getLink(activity)}
                  className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-surface-alt transition-colors group"
                  data-testid={`activity-${activity.id}`}
                >
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage src={activity.actor_avatar} />
                    <AvatarFallback className="bg-brand-primary text-white text-xs">
                      {activity.actor_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary leading-snug">
                      {activity.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Icon className="w-3 h-3 text-text-muted" />
                      <span className="text-xs text-text-muted">{timeAgo(activity.created_at)}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
